import {
    ConversationRequest,
    ConversationRequestWithoutMessages,
    Conversation,
    ChatMessage,
    CosmosDBHealth,
    CosmosDBStatus,
    GlobalSettings,
    Scenario,
    VectorFileRequest,
    VectorFile,
    ConversationFolder,
    ScenarioFolder,
    GptModelSettings,
} from "./models";

export async function downloadConversationFileApi(conversationId: string, fileId: string): Promise<Response> {
    const response = await fetch(
        `/api/file/download?conversationId=${encodeURIComponent(conversationId)}&fileId=${encodeURIComponent(fileId)}`,
        {
            method: "GET",
        },
    );

    return response;
}

export async function getConversationFilesApi(convId: string): Promise<VectorFile[] | null> {
    const response = await fetch(`/api/file?conversationId=${encodeURIComponent(convId)}`, {
        method: "GET",
    })
        .then(async (res) => {
            const payload = await res.json();
            if (!Array.isArray(payload)) {
                console.error("There was an issue fetching your data.");
                return [];
            }
            const files: VectorFile[] = payload.map((file: any) => {
                return {
                    id: file.id,
                    conversationId: file.conversationId,
                    fileName: file.fileName,
                    status: file.status,
                    fileId: file.fileId,
                };
            });
            return files;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return null;
        });

    return response;
}

export async function uploadConversationFileApi(
    options: VectorFileRequest,
    abortSignal: AbortSignal,
): Promise<Response> {
    const formData = new FormData();
    formData.append("file", options.file);
    formData.append("conversationId", options.conversationId);

    const response = await fetch("/api/file", {
        method: "POST",
        body: formData,
        signal: abortSignal,
    });

    return response;
}

export async function deleteConversationFileApi(conversationId: string, fileId: string): Promise<Response> {
    const response = await fetch(
        `/api/file?conversationId=${encodeURIComponent(conversationId)}&fileId=${encodeURIComponent(fileId)}`,
        {
            method: "DELETE",
        },
    );

    return response;
}

export async function getFileStatusApi(conversationId: string, fileId: string): Promise<VectorFile | null> {
    const response = await fetch(
        `/api/file_status?conversationId=${encodeURIComponent(conversationId)}&fileId=${encodeURIComponent(fileId)}`,
        {
            method: "GET",
        },
    )
        .then(async (res) => {
            const payload = await res.json();
            const file: VectorFile = {
                id: payload.id,
                conversationId: payload.conversationId,
                fileName: payload.fileName,
                status: payload.status,
                fileId: payload.fileId,
            };
            return file;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return null;
        });

    return response;
}

export async function fileLimitApi(): Promise<Response> {
    const response = await fetch("/api/file_limit", {
        method: "GET",
    });

    return response;
}

export async function fileUsageApi(): Promise<Response> {
    const response = await fetch("/api/file_usage", {
        method: "GET",
    });

    return response;
}

export async function tokenLimitApi(): Promise<Response> {
    const response = await fetch("/api/token_limit", {
        method: "GET",
    });

    return response;
}

export async function tokenUsageApi(): Promise<Response> {
    const response = await fetch("/api/token_usage", {
        method: "GET",
    });

    return response;
}

export async function loginCheckApi(): Promise<Response> {
    const response = await fetch("/api/login_check", {
        method: "GET",
    });

    return response;
}

export async function logoutApi(): Promise<Response> {
    const response = await fetch("/api/logout", {
        method: "GET",
    });

    return response;
}

export async function createConversationApi(options: ConversationRequestWithoutMessages): Promise<Response> {
    const response = await fetch("/api/conversation/create", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            llm_settings: options.model_settings,
            instruction: options.instruction,
            scenario: options.scenario,
            num_historic_messages: options.num_historic_messages,
            title: options.title,
        }),
    });

    return response;
}

export const historyList = async (offset = 0): Promise<Conversation[] | null> => {
    const response = await fetch(`/api/history/list?offset=${offset}`, {
        method: "GET",
    })
        .then(async (res) => {
            const payload = await res.json();
            if (!Array.isArray(payload)) {
                console.error("There was an issue fetching your data.");
                return null;
            }
            const conversations: Conversation[] = await Promise.all(
                payload.map(async (conv: any) => {
                    const conversation: Conversation = {
                        id: conv.id,
                        title: conv.title,
                        date: conv.createdAt,
                        messages: [],
                        scenario_id: conv.scenario_id ?? undefined,
                        model_settings: conv.llm_settings ?? undefined,
                        num_historic_messages: conv.num_historic_messages ?? 10,
                    };
                    return conversation;
                }),
            );
            return conversations;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return null;
        });

    return response;
};

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
    const response = await fetch("/api/history/read", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            if (!res) {
                return [];
            }
            const payload = await res.json();
            let messages: ChatMessage[] = [];
            if (payload?.messages) {
                payload.messages.forEach((msg: any) => {
                    const message: ChatMessage = {
                        id: msg.id,
                        role: msg.role,
                        date: msg.createdAt,
                        content: msg.content,
                        prompt_tokens: msg.promptTokens ?? undefined,
                        completion_tokens: msg.completionTokens ?? undefined,
                        final_tokens: msg.finalTokens ?? undefined,
                    };
                    messages.push(message);
                });
            }
            return messages;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return [];
        });
    return response;
};

export const historyGenerate = async (
    options: ConversationRequest,
    abortSignal: AbortSignal,
    convId?: string,
): Promise<Response> => {
    let body;
    if (convId) {
        body = JSON.stringify({
            conversation_id: convId,
            messages: options.messages,
            llm_settings: options.model_settings,
            instruction: options.instruction,
            scenario: options.scenario,
            num_historic_messages: options.num_historic_messages,
            folder_id: options.folder_id,
            file_ids: options.file_ids,
        });
    } else {
        body = JSON.stringify({
            messages: options.messages,
            llm_settings: options.model_settings,
            instruction: options.instruction,
            scenario: options.scenario,
            num_historic_messages: options.num_historic_messages,
            folder_id: options.folder_id,
            file_ids: options.file_ids,
        });
    }
    const response = await fetch("/api/history/generate", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: body,
        signal: abortSignal,
    })
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return new Response();
        });
    return response;
};

export const historyUpdate = async (messages: ChatMessage[], convId: string): Promise<Response> => {
    const response = await fetch("/api/history/update", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            messages: messages,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const conversationUpdateWithFile = async (
    messages: ChatMessage[],
    convId: string,
    model_settings: GptModelSettings | undefined = undefined,
    scenario: Scenario | null = null,
): Promise<Conversation | null> => {
    const response = await fetch("/api/history/update/conversationwithfile", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            messages: messages,
            scenario: scenario,
            llm_settings: model_settings,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            if (!res) {
                return null;
            }
            const payload = await res.json();
            const conversation: Conversation = {
                id: payload.id,
                title: payload.title,
                date: payload.createdAt,
                scenario_id: payload.scenario_id ?? undefined,
                model_settings: payload.llm_settings ?? undefined,
                num_historic_messages: payload.num_historic_messages ?? 10,
            };
            return conversation;
        })
        .catch((err) => {
            return null;
        });
    return response;
};

export const historyMoveConversation = async (convId: string, folderId: string): Promise<Response> => {
    const response = await fetch("/api/history/update", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            folder_id: folderId,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyUpdateNumHistoricMessages = async (
    convId: string,
    num_historic_messages: number,
): Promise<Response> => {
    const response = await fetch("/api/history/update_num_historic_messages", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            num_historic_messages: num_historic_messages,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyDelete = async (convId: string): Promise<Response> => {
    const response = await fetch("/api/history/delete", {
        method: "DELETE",
        body: JSON.stringify({
            conversation_id: convId,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyDeleteAll = async (): Promise<Response> => {
    const response = await fetch("/api/history/delete_all", {
        method: "DELETE",
        body: JSON.stringify({}),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyRename = async (convId: string, title: string): Promise<Response> => {
    const response = await fetch("/api/history/rename", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            title: title,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
    const response = await fetch("/api/history/ensure", {
        method: "GET",
    })
        .then(async (res) => {
            let respJson = await res.json();
            let formattedResponse;
            if (respJson.message) {
                formattedResponse = CosmosDBStatus.Working;
            } else {
                if (res.status === 500) {
                    formattedResponse = CosmosDBStatus.NotWorking;
                } else if (res.status === 401) {
                    formattedResponse = CosmosDBStatus.InvalidCredentials;
                } else if (res.status === 422) {
                    formattedResponse = respJson.error;
                } else {
                    formattedResponse = CosmosDBStatus.NotConfigured;
                }
            }
            if (!res.ok) {
                return {
                    cosmosDB: false,
                    status: formattedResponse,
                };
            } else {
                return {
                    cosmosDB: true,
                    status: formattedResponse,
                };
            }
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return {
                cosmosDB: false,
                status: err,
            };
        });
    return response;
};

export const updateGlobalSettings = async (settings: GlobalSettings): Promise<Response> => {
    return await fetch("/api/settings/global/update", {
        method: "POST",
        body: JSON.stringify({
            instruction: settings.instruction,
            showNumHistoricMessages: settings.showNumHistoricMessages,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
};

export const fetchGlobalSettings = async (): Promise<GlobalSettings | null> => {
    try {
        const res = await fetch("/api/settings/global/get", {
            method: "GET",
        });
        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch global settings");
        }
        const payload = await res.json();
        const settings: GlobalSettings = {
            instruction: payload?.instruction,
            showNumHistoricMessages: payload?.showNumHistoricMessages,
        };
        return settings;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const generateScenario = async (scenario: Scenario): Promise<Response> => {
    return await fetch("/api/scenarios/generate", {
        method: "POST",
        body: JSON.stringify(scenario),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            if (!res.ok && res.status === 406) {
                const errorMsg =
                    "Es existiert bereits ein Szenario mit demselben Namen. Bitte einen anderen Namen wählen.";
                let errRes: Response = {
                    ...new Response(),
                    ok: false,
                    status: 406,
                    statusText: errorMsg,
                };
                return errRes;
            }
            return res;
        })
        .catch((err) => {
            let errorMsg = "";
            let errorStatus = 0;
            if (err.status === 406) {
                errorMsg = "Es existiert bereits ein Szenario mit demselben Namen. Bitte einen anderen Namen wählen.";
                errorStatus = err.status;
            } else {
                errorMsg = "Das Szenario konnte in der Datenbank nicht gespeichert werden";
                errorStatus = 500;
            }
            console.error(errorMsg);
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: errorStatus,
                statusText: errorMsg,
            };
            return errRes;
        });
};

export const updateScenario = async (scenario: Scenario): Promise<Response> => {
    return await fetch("/api/scenarios/update", {
        method: "POST",
        body: JSON.stringify(scenario),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            if (!res.ok && res.status === 406) {
                const errorMsg =
                    "Es existiert bereits ein Szenario mit demselben Namen. Bitte einen anderen Namen wählen.";
                let errRes: Response = {
                    ...new Response(),
                    ok: false,
                    status: 406,
                    statusText: errorMsg,
                };
                return errRes;
            }
            return res;
        })
        .catch((err) => {
            let errorMsg = "";
            let errorStatus = 0;
            if (err.status === 406) {
                errorMsg = "Es existiert bereits ein Szenario mit demselben Namen. Bitte einen anderen Namen wählen.";
                errorStatus = err.status;
            } else {
                errorMsg = "Das Szenario konnte in der Datenbank nicht gespeichert werden";
                errorStatus = 500;
            }
            console.error(errorMsg);
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: errorStatus,
                statusText: errorMsg,
            };
            return errRes;
        });
};

export const fetchScenarios = async (): Promise<Scenario[] | null> => {
    try {
        const res = await fetch("/api/scenarios/get", {
            method: "GET",
        });

        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch scenarios");
        }
        const payload = await res.json();
        if (!Array.isArray(payload)) {
            console.error("The fetched data is not an array.");
            return null;
        }

        const scenarios: Scenario[] = payload.map((item: any) => {
            return {
                scenario_id: item.id,
                name: item.name,
                persona: item.persona,
                context: item.context,
                goal: item?.goal,
                output_format: item?.output_format,
                further_instructions: item?.further_instructions,
                folder_id: item?.folderId ?? "-100",
            };
        });

        return scenarios;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const deleteScenario = async (scenario: Scenario): Promise<Response> => {
    return await fetch("/api/scenarios/delete", {
        method: "DELETE",
        body: JSON.stringify(scenario),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue deleting your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
};

export const fetchScenarioFolders = async (): Promise<ScenarioFolder[] | null> => {
    try {
        const res = await fetch("/api/scenariofolder/list", {
            method: "GET",
        });

        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch folders");
        }
        const payload = await res.json();
        if (!Array.isArray(payload)) {
            console.error("The fetched data is not an array.");
            return null;
        }

        const folders: ScenarioFolder[] = payload.map((item: any) => {
            return {
                name: item.folderName,
                id: item.id,
            };
        });

        return folders;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const deleteScenarioFolder = async (folder: ScenarioFolder): Promise<Response> => {
    return await fetch("/api/scenariofolder/delete", {
        method: "DELETE",
        body: JSON.stringify({
            folder_id: folder.id,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue deleting your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
};

export const scenarioFolderGenerate = async (folderName: string): Promise<ScenarioFolder | Response | null> => {
    const body = JSON.stringify({
        name: folderName,
    });
    try {
        const res = await fetch("/api/scenariofolder/generate", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });

        if (!res.ok) {
            return res;
        }
        const payload = await res.json();

        const folder: ScenarioFolder = {
            name: payload.folderName,
            id: payload.id,
        };
        if (folder) {
            return folder;
        }

        return null;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const scenarioFolderRename = async (
    folderId: string,
    folderName: string,
): Promise<ScenarioFolder | Response | null> => {
    try {
        const res = await fetch("/api/scenariofolder/rename", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                folder_id: folderId,
                name: folderName,
            }),
        });

        if (!res.ok) {
            return res;
        }
        const payload = await res.json();

        const folder: ScenarioFolder = {
            name: payload.folderName,
            id: payload.id,
        };
        if (folder) {
            return folder;
        }

        return null;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const fetchScenariosInFolder = async (folderId: string): Promise<Scenario[] | null> => {
    try {
        const res = await fetch(`/api/scenariofolder/scenarios/list?folderId=${folderId}`, {
            method: "GET",
        });

        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch folders");
        }
        const payload = await res.json();
        if (!Array.isArray(payload)) {
            console.error("There was an issue fetching your data.");
            return null;
        }
        const scenarios: Scenario[] = await Promise.all(
            payload.map(async (item: any) => {
                const scenarios: Scenario = {
                    scenario_id: item.id,
                    name: item.name,
                    persona: item.persona,
                    context: item.context,
                    goal: item?.goal,
                    output_format: item?.output_format,
                    further_instructions: item?.further_instructions,
                    folder_id: item?.folderId ?? "-100",
                };
                return scenarios;
            }),
        );
        return scenarios;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const fetchScenariosInNoFolder = async (limit: number = -1, offset: number = 0): Promise<Scenario[] | null> => {
    try {
        let res = null;
        if (limit === -1) {
            res = await fetch(`/api/scenariofolder/scenarios/list?offset=${offset}&noAssignedFolder=true`, {
                method: "GET",
            });
        } else {
            res = await fetch(
                `/api/scenariofolder/scenarios/list?limit=${limit}&offset=${offset}&noAssignedFolder=true`,
                {
                    method: "GET",
                },
            );
        }

        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch folders");
        }
        const payload = await res.json();
        if (!Array.isArray(payload)) {
            console.error("There was an issue fetching your data.");
            return null;
        }
        const scenarios: Scenario[] = await Promise.all(
            payload.map(async (item: any) => {
                const scenarios: Scenario = {
                    scenario_id: item.id,
                    name: item.name,
                    persona: item.persona,
                    context: item.context,
                    goal: item?.goal,
                    output_format: item?.output_format,
                    further_instructions: item?.further_instructions,
                    folder_id: item?.folderId ?? "-100",
                };
                return scenarios;
            }),
        );
        return scenarios;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const fetchConversationFolders = async (): Promise<ConversationFolder[] | null> => {
    try {
        const res = await fetch("/api/historyfolder/list", {
            method: "GET",
        });

        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch folders");
        }
        const payload = await res.json();
        if (!Array.isArray(payload.folders)) {
            console.error("The fetched data is not an array.");
            return null;
        }

        const folders: ConversationFolder[] = payload.folders.map((item: any) => {
            return {
                name: item.folderName,
                id: item.id,
            };
        });

        return folders;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const deleteConversationFolder = async (folder: ConversationFolder): Promise<Response> => {
    return await fetch("/api/historyfolder/delete", {
        method: "DELETE",
        body: JSON.stringify({
            folder_id: folder.id,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then(async (res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue deleting your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
};

export const conversationFolderGenerate = async (folderName: string): Promise<ConversationFolder | null> => {
    const body = JSON.stringify({
        name: folderName,
    });
    try {
        const res = await fetch("/api/historyfolder/generate", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });

        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch folders");
        }
        const payload = await res.json();

        const folder: ConversationFolder = {
            name: payload.folderName,
            id: payload.id,
        };
        if (folder) {
            return folder;
        }

        return null;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const conversationFolderRename = async (
    folderId: string,
    folderName: string,
): Promise<ConversationFolder | null> => {
    try {
        const res = await fetch("/api/historyfolder/rename", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                folder_id: folderId,
                name: folderName,
            }),
        });

        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch folders");
        }

        return {
            name: folderName,
            id: folderId,
        };
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const fetchConversationInFolder = async (folderId: string): Promise<Conversation[] | null> => {
    try {
        const res = await fetch(`/api/historyfolder/conversations/list?folder_id=${folderId}`, {
            method: "GET",
        });

        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch folders");
        }
        const payload = await res.json();
        if (!Array.isArray(payload.conversations)) {
            console.error("There was an issue fetching your data.");
            return null;
        }
        const conversations: Conversation[] = await Promise.all(
            payload.conversations.map(async (conv: any) => {
                const conversation: Conversation = {
                    id: conv.id,
                    title: conv.title,
                    date: conv.createdAt,
                    folder_id: conv.folderId,
                    scenario_id: conv.scenarioId ?? undefined,
                    model_settings: conv.llmSettings ?? undefined,
                    num_historic_messages: conv.numHistoricMessages,
                };
                return conversation;
            }),
        );
        return conversations;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const fetchConversationInNoFolder = async (
    limit: number = -1,
    offset: number = 0,
): Promise<Conversation[] | null> => {
    try {
        let res = null;
        if (limit === -1) {
            res = await fetch(`/api/historyfolder/conversations/list?offset=${offset}&no_assigned_folder=true`, {
                method: "GET",
            });
        } else {
            res = await fetch(
                `/api/historyfolder/conversations/list?limit=${limit}&offset=${offset}&no_assigned_folder=true`,
                {
                    method: "GET",
                },
            );
        }

        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch folders");
        }
        const payload = await res.json();
        if (!Array.isArray(payload.conversations)) {
            console.error("There was an issue fetching your data.");
            return null;
        }
        const conversations: Conversation[] = await Promise.all(
            payload.conversations.map(async (conv: any) => {
                const conversation: Conversation = {
                    id: conv.id,
                    title: conv.title,
                    date: conv.createdAt,
                    scenario_id: conv.scenarioId ?? undefined,
                    model_settings: conv.llmSettings ?? undefined,
                    num_historic_messages: conv.numHistoricMessages,
                };
                return conversation;
            }),
        );
        return conversations;
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const userUpdateAcceptanceStatus = async (acceptanceStatus: boolean): Promise<Response> => {
    const response = await fetch("/api/user/acceptance_status/update", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            acceptanceStatus: acceptanceStatus,
        }),
    })
        .then(async (res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const fetchAcceptanceStatus = async (): Promise<boolean | null> => {
    try {
        const res = await fetch("/api/user/acceptance_status/get", {
            method: "GET",
        });
        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch acceptance status");
        }
        const payload = await res.json();
        if (payload?.acceptedTermsOfUse && payload?.acceptedTermsOfUse == true) {
            const acceptance_status: boolean = payload?.acceptedTermsOfUse;
            return acceptance_status;
        } else {
            const acceptance_status: boolean = false;
            return acceptance_status;
        }
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};

export const getAvailableModels = async (): Promise<string[] | null> => {
    try {
        const res = await fetch("/api/available_models", {
            method: "GET",
        });
        if (!res.ok) {
            console.error("There was an issue fetching your data.");
            throw new Error("Failed to fetch available models");
        }
        const payload = await res.json();
        if (Array.isArray(payload.models)) {
            return payload.models;
        } else {
            console.error("The fetched data is not an array.");
            return null;
        }
    } catch (err) {
        console.error("There was an issue fetching your data.", err);
        return null;
    }
};
